/**
 * @file signInCountryPolicy.ts
 * @description Single source of truth for calling codes accepted by phone sign-in
 * @module features/auth
 * @remarks Picker options are derived from the validator keys so unsupported
 * calling codes cannot become selectable without an explicit validation rule.
 */

import { countryCodesData } from "@/features/auth/components/molecules/CountryCodes/CountryCodesView";

/** A country-specific validator for the formatted value shown in sign-in. */
type PhoneValidator = (value: string) => boolean;

const validatesUsNumber: PhoneValidator = value =>
  /^\(\d{3}\) \d{3}-\d{4}$/.test(value);
const validatesUkNumber: PhoneValidator = value => /^\d{4} \d{6}$/.test(value);
const validatesTwoFourFourNumber: PhoneValidator = value =>
  /^\d{2} \d{4} \d{4}$/.test(value);
const validatesArgentinaNumber: PhoneValidator = value =>
  value.replace(/\D/g, "").length === 10;
const validatesOneFourFourNumber: PhoneValidator = value =>
  /^\d \d{4} \d{4}$/.test(value);
const validatesBrazilNumber: PhoneValidator = value =>
  /^\d{2} \d{5}-\d{4}$/.test(value);
const validatesThreeThreeThreeNumber: PhoneValidator = value =>
  /^\d{3} \d{3} \d{3}$/.test(value);
const validatesGermanyNumber: PhoneValidator = value => {
  const digits = value.replace(/\D/g, "");
  return digits.length >= 10 && digits.length <= 11;
};
const validatesFranceNumber: PhoneValidator = value =>
  /^\d \d{2} \d{2} \d{2} \d{2}$/.test(value);
const validatesIrelandNumber: PhoneValidator = value =>
  /^8\d \d{3} \d{4}$/.test(value);
const validatesItalyNumber: PhoneValidator = value =>
  /^3\d{2} \d{3} \d{4}$/.test(value);
const validatesTwoThreeFourNumber: PhoneValidator = value =>
  /^\d{2} \d{3} \d{4}$/.test(value);
const validatesTwoTwoTwoTwoNumber: PhoneValidator = value =>
  /^\d{2} \d{2} \d{2} \d{2}$/.test(value);
const validatesThreeThreeFourNumber: PhoneValidator = value =>
  /^\d{3} \d{3} \d{4}$/.test(value);
const validatesRussiaNumber: PhoneValidator = value =>
  /^\(\d{3}\) \d{3}-\d{2}-\d{2}$/.test(value);
const validatesSwitzerlandNumber: PhoneValidator = value =>
  /^\d{2} \d{3} \d{2} \d{2}$/.test(value);
const validatesTwoThreeThreeNumber: PhoneValidator = value =>
  /^\d{2} \d{3} \d{3}$/.test(value);
const validatesFourFourNumber: PhoneValidator = value =>
  /^\d{4} \d{4}$/.test(value);

/**
 * Formatted-number validators keyed by calling code.
 * @remarks Object keys define both the accepted validation policy and the
 * picker allowlist; adding a key changes both surfaces together.
 */
/**
 * Calling codes sign-in offers, and the format rule for each.
 *
 * Reduced from 51 to 32 on 2026-08-26, following the August OTP-pumping
 * incident. Nineteen codes were withdrawn: Venezuela (+58) and Bosnia (+387),
 * which the campaign used for $469 of fraudulent sends, plus seventeen whose
 * countries delivered zero legitimate message parts across the seven clean
 * months 2026-01-01 to 2026-07-31.
 *
 * This list is presentation only. Enforcement lives in backend-v2's
 * `SMS_COUNTRY_ALLOWLIST` and in the AWS SMS Protect rule set, both of which
 * reject these destinations regardless of what the picker offers. The value of
 * keeping it in step is that a user is never shown a country they would then
 * be refused for choosing.
 *
 * It cannot be kept perfectly in step, because this file keys on CALLING codes
 * while the enforcement layers key on ISO codes, and the two do not map 1:1.
 * Guernsey, the Isle of Man and Jersey were all withdrawn at the ISO layer but
 * share +44 with the United Kingdom, which is retained — so they remain
 * selectable here and are refused downstream. Watch for the same trap when
 * reasoning about +7, which covers both Russia and Kazakhstan.
 */
const SIGN_IN_PHONE_VALIDATORS = {
  "+1": validatesUsNumber,
  "+7": validatesRussiaNumber,
  "+30": validatesThreeThreeFourNumber,
  "+31": validatesThreeThreeThreeNumber,
  "+32": validatesThreeThreeThreeNumber,
  "+33": validatesFranceNumber,
  "+34": validatesThreeThreeThreeNumber,
  "+36": validatesTwoThreeThreeNumber,
  "+39": validatesItalyNumber,
  "+40": validatesTwoThreeFourNumber,
  "+41": validatesSwitzerlandNumber,
  "+44": validatesUkNumber,
  "+45": validatesTwoTwoTwoTwoNumber,
  "+46": validatesTwoThreeFourNumber,
  "+48": validatesThreeThreeThreeNumber,
  "+49": validatesGermanyNumber,
  "+51": validatesThreeThreeThreeNumber,
  "+52": validatesTwoFourFourNumber,
  "+54": validatesArgentinaNumber,
  "+55": validatesBrazilNumber,
  "+56": validatesOneFourFourNumber,
  "+57": validatesThreeThreeFourNumber,
  "+61": validatesThreeThreeThreeNumber,
  "+90": validatesThreeThreeFourNumber,
  "+351": validatesTwoThreeFourNumber,
  "+353": validatesIrelandNumber,
  "+380": validatesTwoThreeFourNumber,
  "+386": validatesTwoThreeThreeNumber,
  "+420": validatesThreeThreeThreeNumber,
  "+593": validatesTwoThreeFourNumber,
  "+598": validatesFourFourNumber,
  "+966": validatesTwoThreeFourNumber,
} as const satisfies Record<string, PhoneValidator>;

/**
 *
 */
type SupportedSignInCountryCode = keyof typeof SIGN_IN_PHONE_VALIDATORS;

/**
 * Reports whether sign-in has an explicit phone-number rule for a calling code.
 * @param countryCode - International calling prefix including the leading plus
 * @returns True only when sign-in defines a formatted-number validator
 */
export const isSupportedSignInCountryCode = (
  countryCode: string
): countryCode is SupportedSignInCountryCode =>
  Object.hasOwn(SIGN_IN_PHONE_VALIDATORS, countryCode);

/**
 * Validates a formatted phone number using the selected country's rule.
 * @param countryCode - International calling prefix selected by the user
 * @param value - Formatted phone-number value produced by the login input
 * @returns True when the code is supported and the value matches its format
 */
export const validateSignInPhoneNumber = (
  countryCode: string,
  value: string
): boolean =>
  isSupportedSignInCountryCode(countryCode) &&
  SIGN_IN_PHONE_VALIDATORS[countryCode](value);

/**
 * Calling-code records exposed by the sign-in picker.
 * @remarks Filtering the canonical country metadata through validator keys
 * prevents picker and validation support from silently diverging.
 */
export const signInCountryCodesData = countryCodesData.filter(({ code }) =>
  isSupportedSignInCountryCode(code)
);
